/**
 * Utility function to optimize images by resizing and compressing them
 * This helps prevent iOS refresh issues by reducing memory pressure
 */

/**
 * Optimizes an image data URL by resizing and compressing it
 * @param imageDataUrl - Base64 image data URL to optimize
 * @param maxDimension - Maximum width/height dimension (default: 1200px)
 * @param quality - JPEG quality (0-1, default: 0.85)
 * @returns Promise resolving to an optimized image data URL
 */
export const optimizeImageDataUrl = (
  imageDataUrl: string,
  maxDimension = 1200,
  quality = 0.85
): Promise<string> => {
  return new Promise((resolve) => {
    // Create an image to load the data URL
    const img = new Image()

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let newWidth = img.width
      let newHeight = img.height

      if (Math.max(newWidth, newHeight) > maxDimension) {
        if (newWidth > newHeight) {
          newHeight = (newHeight / newWidth) * maxDimension
          newWidth = maxDimension
        } else {
          newWidth = (newWidth / newHeight) * maxDimension
          newHeight = maxDimension
        }
      }

      // Create a canvas to draw the resized image
      const canvas = document.createElement('canvas')
      canvas.width = newWidth
      canvas.height = newHeight

      // Draw the image on the canvas
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(img, 0, 0, newWidth, newHeight)
      }

      // Convert to an optimized JPEG with reduced quality
      const optimizedDataUrl = canvas.toDataURL('image/jpeg', quality)

      // Clean up to prevent memory leaks
      img.onload = null
      img.src = ''

      resolve(optimizedDataUrl)
    }

    // Handle errors
    img.onerror = () => {
      console.error('Failed to load image for optimization')
      resolve(imageDataUrl) // Return original if optimization fails
    }

    // Start loading the image
    img.src = imageDataUrl
  })
}

/**
 * Optimizes a File object by resizing and compressing it
 * @param file - File object to optimize
 * @param maxDimension - Maximum width/height dimension (default: 1200px)
 * @param quality - JPEG quality (0-1, default: 0.85)
 * @returns Promise resolving to an optimized File object
 */
export const optimizeImageFile = (
  file: File,
  maxDimension = 1200,
  quality = 0.85
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = async (event) => {
      if (!event.target?.result) {
        reject(new Error('Failed to read file'))
        return
      }

      try {
        const dataUrl = event.target.result as string
        const optimizedDataUrl = await optimizeImageDataUrl(
          dataUrl,
          maxDimension,
          quality
        )

        // Convert data URL to blob
        const response = await fetch(optimizedDataUrl)
        const blob = await response.blob()

        // Create a new File object
        const optimizedFile = new File([blob], file.name, {
          type: 'image/jpeg',
        })

        resolve(optimizedFile)
      } catch (error) {
        console.error('Error optimizing image file:', error)
        resolve(file) // Return original file if optimization fails
      }
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * Safely updates image state with optimized images and prevents iOS refresh issues
 * @param image - The image data or file to process
 * @param updateFn - Function to call with the optimized image
 * @param mountedRef - React ref to check if component is still mounted (optional)
 */
export const safelyUpdateImageState = async (
  image: string | File,
  updateFn: (optimizedImage: string | File) => void,
  mountedRef?: React.RefObject<boolean>
) => {
  try {
    // Optimize the image
    let optimizedImage: string | File

    if (typeof image === 'string') {
      optimizedImage = await optimizeImageDataUrl(image)
    } else {
      optimizedImage = await optimizeImageFile(image)
    }

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      // Delay state update slightly to prevent iOS refresh trigger
      setTimeout(() => {
        if (!mountedRef || mountedRef.current) {
          updateFn(optimizedImage)
        }
      }, 150) // Slightly longer delay for iOS stability
    })

    return optimizedImage
  } catch (error) {
    console.error('Error in image update process:', error)
    return image // Return original if optimization fails
  }
}
